import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import moment from "moment";
import momentTz from "moment-timezone";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  GoogleMap,
  Marker,
  Autocomplete,
  useJsApiLoader,
} from "@react-google-maps/api";

import { updateFixture } from "../../redux/slices/tournamentSlice";
import { fixtureEdited } from "../../utils/analytics";
import ClubbieModal from "../Modal/ClubbieModal";
import ClubbieSelect from "../ClubbieSelect/ClubbieSelect";
import DatePicker from "../DatePicker/DatePicker";
import TimePicker from "../TimePicker/TimePicker";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import Checkbox from "../Checkbox/Checkbox";
import {
  buildSlotOptions,
  findCurrentSlotOption,
  isSlotEligible,
  NO_LINK_VALUE,
} from "../../pages/fixtures/utils/buildSlotOptions";

// Pitch on a fixture is stored as `"Pitch <n>"` (matching auto-gen output).
// The UI captures just the number; format/parse helpers bridge the two.
const formatPitchValue = (n) => {
  const num = parseInt(n, 10);
  if (!Number.isFinite(num) || num < 1) return undefined;
  return `Pitch ${num}`;
};

const parsePitchValue = (s) => {
  if (!s) return "";
  const match = String(s).match(/(\d+)/);
  return match ? match[1] : "";
};

const MAP_STYLES = [
  { featureType: "all", elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { featureType: "all", elementType: "labels.text.stroke", stylers: [{ lightness: -80 }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#263c3f" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca5b3" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
];

/**
 * EditFixtureModal - Modal for editing an existing fixture
 * Matches Figma design with team display, date/time pickers, pitch and referee selectors
 */
const EditFixtureModal = ({ isOpen, onClose, fixture, onSuccess }) => {
  const dispatch = useDispatch();
  const { selectedTournament, selectedCategory } = useSelector(
    (state) => state.tournament
  );

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    matchDate: "",
    matchStartTime: "",
    matchEndTime: "",
    pitch: "",
    referees: [],
  });

  // Phase + slot-link selectors. Initialised from the fixture's current
  // location in the useEffect below. `selectedSlot === null` means the
  // fixture should land in the Custom Matches block of `selectedPhase`
  // (i.e. the "No link" sentinel).
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Per-fixture venue override. `useOverride` toggles the optional section;
  // when off (or override has no coords), the fixture inherits the
  // tournament's matchVenue at publish time.
  const [useOverride, setUseOverride] = useState(false);
  const [overrideVenueName, setOverrideVenueName] = useState("");
  const [overrideLocation, setOverrideLocation] = useState(null); // { lat, lng }
  const [overrideAddress, setOverrideAddress] = useState("");
  const [overrideSearchQuery, setOverrideSearchQuery] = useState("");
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { isLoaded: isMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  // Initialize form data when fixture changes.
  // matchTime is a UTC instant (ISO string from the API). Render the date and
  // time inputs in the viewer's local timezone so the user edits wall-clock
  // values that match what they see elsewhere.
  useEffect(() => {
    if (fixture) {
      const localMoment = fixture.matchTime ? moment(fixture.matchTime) : null;
      setFormData({
        matchDate: localMoment ? localMoment.format("YYYY-MM-DD") : "",
        matchStartTime: localMoment ? localMoment.format("HH:mm") : "",
        matchEndTime: fixture.matchEndTime || "",
        pitch: parsePitchValue(fixture.pitch),
        referees: fixture.referees?.map((r) => ({
          label: r.name || r.userId?.profile?.fullName || "Unknown Referee",
          value: r.userId?.id || r.userId?._id || r.userId,
        })) || [],
      });

      const ov = fixture.venueOverride;
      const ovLat = ov?.location?.lat;
      const ovLng = ov?.location?.lng;
      const hasOv = ov && ovLat != null && ovLng != null;
      setUseOverride(!!hasOv);
      setOverrideVenueName(ov?.venue || "");
      setOverrideLocation(hasOv ? { lat: ovLat, lng: ovLng } : null);
      setOverrideAddress(ov?.address || "");
      setOverrideSearchQuery("");

      // Initialise phase + slot-link selectors from the fixture's current
      // location in the tournament tree.
      const phases = selectedCategory?.phases || [];
      const currentPhase = phases.find(
        (p) => (p._id || p.id)?.toString() === fixture.phaseId?.toString()
      );
      if (currentPhase) {
        const phaseOpt = {
          label: currentPhase.name,
          value: currentPhase._id || currentPhase.id,
          _phase: currentPhase,
        };
        setSelectedPhase(phaseOpt);
        // Derive the current slot by running the same enumeration we use for
        // the dropdown options and finding the entry that matches the
        // fixture's location. Returns null for fixtures in the Custom
        // Matches block (treated as "No link").
        const opts = buildSlotOptions(phaseOpt);
        setSelectedSlot(findCurrentSlotOption(opts, fixture));
      } else {
        setSelectedPhase(null);
        setSelectedSlot(null);
      }
    }
  }, [fixture, selectedCategory]);

  // Reverse-geocode the override's stored lat/lng when the modal opens, so the
  // address text field mirrors the pin (matchVenue persists coords only on old
  // tournaments; venueOverride should always store address but we reverse-
  // geocode defensively in case it's missing).
  useEffect(() => {
    if (!isOpen) return;
    if (overrideAddress) return;
    if (!isMapsLoaded || !window.google?.maps?.Geocoder) return;
    if (!overrideLocation) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: overrideLocation }, (results, status) => {
      if (status === "OK" && results[0]) {
        setOverrideAddress(results[0].formatted_address);
      }
    });
  }, [isOpen, isMapsLoaded, overrideLocation, overrideAddress]);

  // Referee options
  const refereeOptions = useMemo(() => {
    const refs = selectedTournament?.members?.filter((m) => m.role === "referee") || [];
    return refs
      .filter((r) => r.userId?.profile)
      .map((r) => ({
        label: r.userId.profile.fullName,
        value: r.userId.id,
      }));
  }, [selectedTournament]);

  // Phase options for the move selector — all phases in the category.
  const phaseOptions = useMemo(
    () => (selectedCategory?.phases || []).map((p) => ({
      label: p.name,
      value: p._id || p.id,
      _phase: p,
    })),
    [selectedCategory]
  );

  // Slot options for the selected phase — enumerate every link target
  // (group / bracket tie per leg / non-custom single match) plus the
  // "No link" sentinel. We then filter out GROUP options whose membership
  // doesn't include both of the fixture's teams (ClubbieSelect doesn't
  // support disabled-option rendering, so filter is cleaner than disable).
  const slotOptions = useMemo(() => {
    const homeId = (fixture?.homeTeamActualId?._id || fixture?.homeTeamActualId);
    const awayId = (fixture?.awayTeamActualId?._id || fixture?.awayTeamActualId);
    return buildSlotOptions(selectedPhase).filter((opt) =>
      isSlotEligible(opt, { homeTeamId: homeId, awayTeamId: awayId })
    );
  }, [selectedPhase, fixture]);

  const handleDateChange = useCallback((date) => {
    setFormData((prev) => ({ ...prev, matchDate: date }));
  }, []);

  const handleTimeChange = useCallback((time, field) => {
    setFormData((prev) => ({ ...prev, [field]: time }));
  }, []);

  const handlePitchChange = useCallback((value) => {
    const digits = value.replace(/\D/g, "");
    setFormData((prev) => ({ ...prev, pitch: digits }));
  }, []);

  const handleRefereesChange = useCallback((option) => {
    // Convert single selection to array for consistency
    setFormData((prev) => ({ ...prev, referees: option ? [option] : [] }));
  }, []);

  const handleOverridePlaceSelected = useCallback((place) => {
    if (!place?.geometry?.location) return;
    const loc = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    setOverrideLocation(loc);
    setOverrideAddress(place.formatted_address || "");
    setOverrideSearchQuery(place.formatted_address || "");
    if (mapRef.current) {
      mapRef.current.panTo(loc);
      mapRef.current.setZoom(15);
    }
  }, []);

  const handleOverrideMapClick = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    const loc = { lat, lng };
    setOverrideLocation(loc);
    if (window.google?.maps?.Geocoder) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: loc }, (results, status) => {
        if (status === "OK" && results[0]) {
          setOverrideAddress(results[0].formatted_address);
        }
      });
    }
  }, []);

  const handleToggleOverride = useCallback((checked) => {
    setUseOverride(checked);
    if (!checked) {
      // Clear local override state so save sends null to remove it.
      setOverrideVenueName("");
      setOverrideLocation(null);
      setOverrideAddress("");
      setOverrideSearchQuery("");
    }
  }, []);

  const handleSave = async () => {
    if (!fixture) return;

    setIsLoading(true);
    const loadingToast = toast.loading("Updating fixture...");

    try {
      // Combine the user's local date+time pair into a UTC instant using
      // their browser timezone. Backend stores matchTime as a UTC Date.
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const matchTimeUtc = momentTz
        .tz(`${formData.matchDate} ${formData.matchStartTime}`, "YYYY-MM-DD HH:mm", timezone)
        .toISOString();

      // Build the per-fixture venue override. When the toggle is off, or the
      // user enabled it but never picked coords, we send `null` so the backend
      // clears any previous override and the fixture falls back to the
      // tournament's matchVenue.
      let venueOverride = null;
      if (useOverride && overrideLocation?.lat != null && overrideLocation?.lng != null) {
        venueOverride = {
          venue: overrideVenueName.trim() || overrideAddress || "",
          address: overrideAddress || "",
          location: { lat: overrideLocation.lat, lng: overrideLocation.lng },
          mapLink: `https://www.google.com/maps?q=${overrideLocation.lat},${overrideLocation.lng}`,
        };
      }

      // Detect whether the user changed phase or slot. Encode the move
      // intent for the backend:
      //   - targetPhaseId: when the destination phase differs.
      //   - targetBlockId: when the destination block differs (null = unlink
      //     to Custom Matches block).
      //   - targetFixtureId: only used when the destination is a bracket tie.
      const currentPhaseId = fixture.phaseId?.toString();
      const currentBlockId = fixture.blockId?.toString();
      const currentFixtureId = (fixture._id || fixture.id)?.toString();
      const chosenPhaseId = selectedPhase?.value?.toString();

      // Resolve the "current slot" encoded the same way as `selectedSlot` so
      // changes can be diffed without ambiguity. A fixture sitting in the
      // Custom Matches block reads as `null` (the No-link sentinel).
      const currentBlockInPhase = selectedPhase?._phase?.blocks?.find(
        (b) => (b._id || b.id)?.toString() === currentBlockId
      );
      const currentIsLinked = !!currentBlockInPhase && !currentBlockInPhase.isCustom;

      const chosenIsLinked = !!selectedSlot && selectedSlot.kind !== "noLink";
      const chosenBlockId = chosenIsLinked ? selectedSlot.blockId : null;
      const chosenFixtureId = selectedSlot?.kind === "bracket" ? selectedSlot.fixtureId : null;

      const phaseChanged = chosenPhaseId && chosenPhaseId !== currentPhaseId;
      const blockChanged = chosenBlockId !== (currentIsLinked ? currentBlockId : null);
      const fixtureChanged = selectedSlot?.kind === "bracket"
        ? chosenFixtureId !== currentFixtureId
        : false;

      const payload = {
        matchTime: matchTimeUtc,
        pitch: formatPitchValue(formData.pitch),
        referees: formData.referees.map((r) => ({ userId: r.value })),
        venueOverride,
        ...(phaseChanged && { targetPhaseId: chosenPhaseId }),
        ...((blockChanged || phaseChanged || fixtureChanged) && {
          targetBlockId: chosenBlockId,
        }),
        ...(selectedSlot?.kind === "bracket" && {
          targetFixtureId: chosenFixtureId,
        }),
      };

      await dispatch(
        updateFixture({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategory._id,
          phaseId: fixture.phaseId,
          blockId: fixture.blockId,
          fixtureId: fixture._id,
          fixtureData: payload,
        })
      ).unwrap();

      fixtureEdited({
        fixtureId: fixture._id,
        tournamentId: selectedTournament.id,
      });
      toast.dismiss(loadingToast);
      toast.success("Fixture updated successfully!");
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Failed to update fixture");
    } finally {
      setIsLoading(false);
    }
  };

  const getAvailableDates = useCallback(() => {
    const { matchSchedule } = selectedTournament || {};
    if (!matchSchedule) return [];

    const startDate = moment(matchSchedule.startDate);
    const endDate = moment(matchSchedule.endDate);
    const allowedDays = matchSchedule.matchDays || [];

    const dates = [];
    let currentDate = startDate.clone();

    while (currentDate.isSameOrBefore(endDate)) {
      const dayName = currentDate.format("dddd");
      if (allowedDays.length === 0 || allowedDays.includes(dayName)) {
        dates.push(currentDate.format("YYYY-MM-DD"));
      }
      currentDate.add(1, "day");
    }

    return dates;
  }, [selectedTournament]);

  if (!fixture) return null;

  const footer = (
    <div className="flex justify-end gap-3 w-full">
      <button
        onClick={onClose}
        className="px-6 py-3 text-[15px] font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
      >
        Cancel
      </button>
      <button
        onClick={handleSave}
        disabled={isLoading}
        className="bg-[#00E7FF] hover:opacity-50 text-[#021529] font-medium px-6 py-3 rounded-full text-[15px] transition-opacity disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {isLoading ? <><LoadingSpinner size="small" /><span>Saving...</span></> : "Save"}
      </button>
    </div>
  );

  return (
    <ClubbieModal
      isOpen={isOpen}
      toggle={onClose}
      title="Edit Fixture"
      width="560px"
      maxHeight="90vh"
      footer={footer}
    >
      <div className="space-y-5 py-1">
        {/* Teams Display (Read-only) */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <p className="text-[15px] font-medium text-[#FEFCEB] m-0">Home Team</p>
            <div className="h-11 bg-[#021529] border border-[#192634] rounded-full px-4 flex items-center text-[#FEFCEB] text-[15px]">
              {fixture.homeTeam?.title || "Team 1"}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-[15px] font-medium text-[#FEFCEB] m-0">Away Team</p>
            <div className="h-11 bg-[#021529] border border-[#192634] rounded-full px-4 flex items-center text-[#FEFCEB] text-[15px]">
              {fixture.awayTeam?.title || "Team 2"}
            </div>
          </div>
        </div>

        {/* Phase (move target) */}
        <div className="space-y-2">
          <p className="text-[15px] font-medium text-[#FEFCEB] m-0">Phase</p>
          <ClubbieSelect
            placeholder="Select phase"
            options={phaseOptions}
            value={selectedPhase}
            onChange={(opt) => {
              setSelectedPhase(opt || null);
              // Slot is scoped to the chosen phase, so any previous pick is
              // meaningless when the phase changes.
              setSelectedSlot(null);
            }}
            getOptionLabel={(opt) => opt?.label || ""}
            getOptionValue={(opt) => opt?.value || ""}
          />
        </div>

        {/* Link to existing match — rendered only when the selected phase has
            at least one linkable slot (group / bracket tie / non-custom
            single match). Picking "No link" unlinks back to a standalone
            custom match. GROUP options whose members don't include the
            fixture's current teams are filtered out (see slotOptions). */}
        {slotOptions.length > 1 && (
          <div className="space-y-2">
            <p className="text-[15px] font-medium text-[#FEFCEB] m-0">
              Link to existing match (optional)
            </p>
            <ClubbieSelect
              placeholder="Standalone custom match"
              options={slotOptions}
              value={selectedSlot}
              onChange={(opt) => {
                const next = opt && opt.value !== NO_LINK_VALUE ? opt : null;
                setSelectedSlot(next);
              }}
              getOptionLabel={(opt) => opt?.label || ""}
              getOptionValue={(opt) => opt?.value || ""}
            />
          </div>
        )}

        {/* Match Date */}
        <div className="space-y-2">
          <p className="text-[15px] font-medium text-[#FEFCEB] m-0">Match Date</p>
          <DatePicker
            value={formData.matchDate}
            onChange={handleDateChange}
            minDate={selectedTournament?.matchSchedule?.startDate}
            maxDate={selectedTournament?.matchSchedule?.endDate}
            availableDates={getAvailableDates()}
          />
        </div>

        {/* Match Times */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <p className="text-[15px] font-medium text-[#FEFCEB] m-0">Start Time</p>
            <TimePicker
              value={formData.matchStartTime}
              onChange={(time) => handleTimeChange(time, "matchStartTime")}
            />
          </div>
          <div className="space-y-2">
            <p className="text-[15px] font-medium text-[#FEFCEB] m-0">End Time</p>
            <TimePicker
              value={formData.matchEndTime}
              onChange={(time) => handleTimeChange(time, "matchEndTime")}
            />
          </div>
        </div>

        {/* Pitch */}
        <div className="space-y-2">
          <p className="text-[15px] font-medium text-[#FEFCEB] m-0">Pitch</p>
          <input
            type="number"
            min="1"
            step="1"
            value={formData.pitch}
            onChange={(e) => handlePitchChange(e.target.value)}
            placeholder="Pitch number (e.g. 1)"
            className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary focus:border-blue-shade-2 outline-none transition-colors"
          />
        </div>

        {/* Referees */}
        <div className="space-y-2">
          <p className="text-[15px] font-medium text-[#FEFCEB] m-0">Referee</p>
          <ClubbieSelect
            placeholder="Select referee"
            options={refereeOptions}
            value={formData.referees?.[0] || null}
            onChange={handleRefereesChange}
            getOptionLabel={(opt) => opt?.label || ''}
            getOptionValue={(opt) => opt?.value || ''}
          />
        </div>

        {/* Per-fixture location override */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={useOverride}
              onChange={(e) => handleToggleOverride(e.target.checked)}
            />
            <p className="text-[15px] font-medium text-[#FEFCEB] m-0">
              Use a different location for this match
            </p>
          </div>
          <p className="text-[13px] text-app-text-secondary m-0">
            {useOverride
              ? "This fixture will use the location below instead of the tournament venue."
              : `Using tournament venue${selectedTournament?.matchVenue?.venue ? `: ${selectedTournament.matchVenue.venue}` : ""}.`}
          </p>

          {useOverride && (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] text-app-text-secondary">Venue name</label>
                <input
                  type="text"
                  value={overrideVenueName}
                  onChange={(e) => setOverrideVenueName(e.target.value)}
                  placeholder="Venue name"
                  className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary focus:border-blue-shade-2 outline-none transition-colors"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] text-app-text-secondary">Address</label>
                <input
                  type="text"
                  value={overrideAddress}
                  readOnly
                  placeholder="Pick a location on the map"
                  className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary cursor-default"
                />
              </div>

              {isMapsLoaded && (
                <div className="relative w-full h-[280px] rounded-[20px] overflow-hidden border border-[#192634]">
                  <div className="absolute top-3 left-3 right-3 z-10">
                    <Autocomplete
                      onLoad={(ac) => { autocompleteRef.current = ac; }}
                      onPlaceChanged={() => {
                        if (autocompleteRef.current) handleOverridePlaceSelected(autocompleteRef.current.getPlace());
                      }}
                      options={{ types: ["establishment", "geocode"] }}
                    >
                      <input
                        type="text"
                        placeholder="Search location..."
                        value={overrideSearchQuery}
                        onChange={(e) => setOverrideSearchQuery(e.target.value)}
                        className="w-full bg-white text-black rounded-md px-3 py-2 text-sm focus:outline-none shadow-lg"
                      />
                    </Autocomplete>
                  </div>

                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={overrideLocation || (selectedTournament?.matchVenue?.location?.lat && selectedTournament?.matchVenue?.location?.lng
                      ? { lat: selectedTournament.matchVenue.location.lat, lng: selectedTournament.matchVenue.location.lng }
                      : { lat: 51.5074, lng: -0.1278 })}
                    zoom={overrideLocation ? 15 : 10}
                    onClick={handleOverrideMapClick}
                    onLoad={(map) => { mapRef.current = map; }}
                    options={{ disableDefaultUI: false, zoomControl: true, streetViewControl: false, mapTypeControl: false, fullscreenControl: false, styles: MAP_STYLES }}
                  >
                    {overrideLocation && <Marker position={overrideLocation} />}
                  </GoogleMap>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </ClubbieModal>
  );
};

export default EditFixtureModal;
